// COCHE §10.1.10 — vue-i18n setup. The selected locale is persisted in a
// cookie (`imles-locale`, 1-year TTL) so it survives across browser sessions
// and authenticated/unauthenticated transitions. localStorage is mirrored
// for legacy compatibility but the cookie is the source of truth — and is
// the only thing the unauthenticated /login page reads.
//
// The selection is surfaced to the backend via an axios interceptor that
// adds the X-Imles-Locale header on every request.
import { createI18n } from "vue-i18n";
import en_hk from "./locales/en_hk";
import zh_hk from "./locales/zh_hk";
import zh_cn from "./locales/zh_cn";

export const SUPPORTED_LOCALES = ["en_hk", "zh_hk", "zh_cn"] as const;
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

export const LOCALE_STORAGE_KEY = "imles-locale";
export const LOCALE_COOKIE_KEY = "imles-locale";
const COOKIE_TTL_DAYS = 365;

function readCookie(name: string): string | null {
	if (typeof document === "undefined") return null;
	const prefix = `${name}=`;
	const parts = document.cookie ? document.cookie.split(";") : [];
	for (let raw of parts) {
		raw = raw.trim();
		if (raw.startsWith(prefix)) {
			return decodeURIComponent(raw.substring(prefix.length));
		}
	}
	return null;
}

function writeCookie(name: string, value: string, days: number) {
	if (typeof document === "undefined") return;
	const expires = new Date();
	expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
	document.cookie =
		`${name}=${encodeURIComponent(value)};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
}

export function getCurrentLocale(): SupportedLocale {
	const fromCookie = readCookie(LOCALE_COOKIE_KEY);
	if (fromCookie && (SUPPORTED_LOCALES as readonly string[]).includes(fromCookie)) {
		return fromCookie as SupportedLocale;
	}
	if (typeof window !== "undefined") {
		const fromStorage = window.localStorage.getItem(LOCALE_STORAGE_KEY);
		if (fromStorage && (SUPPORTED_LOCALES as readonly string[]).includes(fromStorage)) {
			// Promote legacy localStorage value to cookie so the rest of the
			// stack can rely on the cookie being authoritative.
			writeCookie(LOCALE_COOKIE_KEY, fromStorage, COOKIE_TTL_DAYS);
			return fromStorage as SupportedLocale;
		}
	}
	return "zh_cn";
}

// Locale-appropriate dayjs format strings for el-date-picker `format` props.
// The Element Plus locale pack itself switches in App.vue (el-config-provider),
// but each picker's explicit `format` prop overrides it — so the date field
// components must derive their format from the current locale instead of
// hardcoding the Chinese "YYYY 年 MM 月 DD 日" style. en_hk -> Hong-Kong-English
// "24 Oct 2025"; zh_cn / zh_hk keep the Chinese 年/月/日 style.
export function datePickerFormat(): string {
	return getCurrentLocale() === "en_hk" ? "DD MMM YYYY" : "YYYY 年 MM 月 DD 日";
}

export function dateTimePickerFormat(): string {
	return getCurrentLocale() === "en_hk"
		? "DD MMM YYYY HH:mm:ss"
		: "YYYY 年 MM 月 DD 日 HH:mm:ss";
}

export function setCurrentLocale(locale: SupportedLocale, reload = true): void {
	writeCookie(LOCALE_COOKIE_KEY, locale, COOKIE_TTL_DAYS);
	if (typeof window !== "undefined") {
		window.localStorage.setItem(LOCALE_STORAGE_KEY, locale);
	}
	i18n.global.locale.value = locale;
	if (reload) {
		// Force a reload so any cached server responses (project schema etc.)
		// are refetched with the new locale header.
		window.location.reload();
	}
}

const i18n = createI18n({
	legacy: false,
	locale: getCurrentLocale(),
	fallbackLocale: "zh_cn",
	messages: {
		en_hk,
		zh_hk,
		zh_cn,
	},
});

export default i18n;
